// Promos de la tienda en «Elige el teléfono»: cajas del encabezado, lista corta de cada tarjeta y
// promos sobre accesorios del detalle, sacadas de las promos de la tienda. Puro, sin React.
import { Gift, Tag, Ticket, Wallet } from 'lucide-react';
import type { CardPromo } from '@/domains/sales/wizard/components/CardPricing';
import type { AccessoryPromotionNote } from '@/domains/sales/wizard/components/ProductDetailCard';
import type { StorePromoLine } from '@/domains/sales/wizard/components/StorePromosBanner';
import type {
    SalePromotionTone,
    StorePromotionGroups,
} from '@/domains/sales/wizard/promotionSlots';
import type { CatalogItem } from '@/domains/sales/wizard/types';
import type { StoreSalePromotionItem } from '../api/wizardPromotionsContract';
import { isWholeStore } from './phoneDraft';
import { appliesToItem, isSureKeepOnly } from './promotionAppliesTo';
import { discountLabel, reachesItem } from './saleBenefits';

type Levers = StoreSalePromotionItem['levers'];

/** Solo baja accesorios: no cambia el precio del teléfono. */
export function isAccessoryOnly(promo: Pick<StoreSalePromotionItem, 'levers'>): boolean {
    const { accessoryDiscount, ...rest } = promo.levers;
    return accessoryDiscount !== null && Object.values(rest).every((lever) => lever === null);
}

/** Tono de la palanca principal: descuento, regalo, beneficio y cupón, en ese orden. */
export function toneOfStoreLevers(levers: Levers): SalePromotionTone {
    if (levers.deviceDiscount || levers.accessoryDiscount) return 'discount';
    if (levers.gift) return 'gift';
    if (levers.walletBonus || levers.airtime || levers.extendedWarranty) return 'benefit';
    return levers.coupon ? 'coupon' : 'benefit';
}

const TONE_ICON = { discount: Tag, gift: Gift, benefit: Wallet, coupon: Ticket } as const;

const byName = (a: { name: string }, b: { name: string }) => a.name.localeCompare(b.name, 'es-MX');

function lineOf(promo: StoreSalePromotionItem): StorePromoLine {
    return { key: promo.promotionId, name: promo.name, customerText: promo.customerText || null };
}

/** «En venta»: solo las de toda la tienda; «Sobre accesorios»: las que solo bajan accesorios. */
export function storePromotionGroupsOf(
    promotions: readonly StoreSalePromotionItem[],
): StorePromotionGroups {
    return {
        sale: promotions
            .filter((p) => isWholeStore(p) && !isAccessoryOnly(p))
            .sort(byName)
            .map(lineOf),
        accessories: promotions.filter(isAccessoryOnly).sort(byName).map(lineOf),
    };
}

/** Promos propias de la tarjeta, A-Z: las que alcanzan su SKU, sin las de toda la tienda. */
export function cardPromotionsOf(
    item: CatalogItem,
    promotions: readonly StoreSalePromotionItem[],
): CardPromo[] {
    return promotions
        .filter((p) => !isWholeStore(p) && !isAccessoryOnly(p) && reachesItem(item, p))
        .sort(byName)
        .map((promo) => {
            const tone = toneOfStoreLevers(promo.levers);
            const device = promo.levers.deviceDiscount;
            const title = isSureKeepOnly(promo)
                ? `${promo.name} · Solo con crédito SureKeep`
                : promo.name;
            return {
                key: `sale-${promo.promotionId}`,
                label: promo.name,
                icon: TONE_ICON[tone],
                title,
                ...(tone !== 'discount' && { tone }),
                ...(device && { amount: discountLabel(device) }),
            };
        });
}

/** Promos sobre accesorios que el teléfono activa; se aplican en «Arma la venta». */
export function accessoryPromotionsOf(
    item: CatalogItem,
    promotions: readonly StoreSalePromotionItem[],
): AccessoryPromotionNote[] {
    return promotions
        .filter((p) => isAccessoryOnly(p) && p.anchorBound && appliesToItem(item, p))
        .sort(byName)
        .map((p) => ({ promotionId: p.promotionId, name: p.name }));
}
